import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { WeeklyInsight, Meal, Symptom, SuspectedTriggerItem, LongitudinalIntoleranceItem, Exercise, UserSession } from '../lib/types';
import { SymptomAnalyticsView } from './SymptomAnalyticsView';
import { SymptomSummaryCard } from './SymptomSummaryCard';

const DoctorSummaryModal = React.lazy(() => import('./DoctorSummaryModal').then((m) => ({ default: m.DoctorSummaryModal })));
import {
  computeIngredientStatistics,
  computeLongitudinalIntoleranceStats,
  computeFodmapSubtypeCorrelations,
  computeMotilityKinetics,
  getTransitPhaseLabel,
  extractFoodsFromMeal,
  isFlareSymptom,
  getClinicalCategory,
  inferTransitSpeedFromSymptoms,
  generateSafeRecoveryMeals,
  SafeRecoveryMealSuggestion,
} from '../lib/correlationEngine';
import { formatIsoDate, formatTime, formatFullDate, calculateLagHours } from '../lib/dateUtils';
import { localStore } from '../lib/storage';
import { triggerHaptic } from '../lib/haptics';
import { GutScoreGauge } from './insights/GutScoreGauge';
import { InsightSummaryCard } from './insights/InsightSummaryCard';
import { TriggerList } from './insights/TriggerList';
import { PatternList } from './insights/PatternList';
import { WeeklyPlanList } from './insights/WeeklyPlanList';
import { MoreAnalysisList, type MoreAnalysisPage } from './insights/MoreAnalysisList';
import { InsightsSubPage, OverlayLayer } from './insights/InsightsSubPage';
import { PRESS } from './insights/insightsUi';
import { computeGutScore, gutScoreReason } from '../lib/gutScore';
import { briefOnlySummary, finalPlan } from '../lib/insightAssemble';
import { deriveInsightView, type InsightView } from '../lib/insightClient';
import {
  BarChart3,
  Activity,
  AlertTriangle,
  RefreshCw,
  Clock,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  TrendingDown,
  TrendingUp,
  ShieldAlert,
  Droplets,
  CheckCircle2,
  Info,
  Flame,
  ShieldCheck,
  Maximize2,
  X,
  ClipboardList,
  Edit3,
  Utensils,
  Sun,
  Moon,
  Apple,
  Coffee,
  Filter,
  Layers,
  FileText,
  HeartPulse,
  Plus,
  ArrowRight,
} from 'lucide-react';

interface InsightsViewProps {
  insights: WeeklyInsight[];
  meals: Meal[];
  symptoms: Symptom[];
  exercises?: Exercise[];
  user?: UserSession | null;
  isAiRunning: boolean;
  onReanalyze: () => Promise<WeeklyInsight>;
  onEditMeal?: (meal: Meal) => void;
  onEditSymptom?: (symptom: Symptom) => void;
  onLogMeal?: (meal: { name: string; ingredients: string[] }) => void;
}

export type ChartMetric = 'bloat' | 'soreness' | 'stool';
export type ChartTimeframe = 'day' | '3days' | 'week' | 'month';

export const INTOLERANCE_CATEGORIES = [
  {
    id: 'gluten',
    name: 'Gluten / Wheat',
    match: (ing: string) => {
      const lower = (ing || '').toLowerCase();
      if (
        /(rice|corn|almond|coconut|tapioca|cassava|arrowroot|potato|buckwheat|oat|sorghum|millet|teff|chickpea|maize|gluten[- ]free|gf)\s*(flour|bread|pasta|noodles?|grain)?/i.test(
          lower
        )
      ) {
        return false;
      }
      return /\b(wheat|gluten|bread|pasta|sourdough|flour|barley|rye|spelt|couscous|semolina|noodle|noodles|croissant|bagel|pancake|pancakes|waffle|waffles|pastry|cake|cracker|crackers|toast)\b/i.test(
        lower
      );
    },
  },
  {
    id: 'dairy',
    name: 'Dairy / Lactose',
    match: (ing: string) =>
      /\b(milk|cheese|dairy|butter|yogurt|yoghurt|cream|whey|casein|ghee|parmesan|mozzarella|cheddar|feta|mascarpone|ricotta|lactose)\b/i.test(
        ing
      ) && !/(oat|almond|soy|soya|coconut|rice|cashew)\s*milk/i.test(ing),
  },
  {
    id: 'fodmap',
    name: 'FODMAPs / Alliums',
    match: (ing: string) =>
      /\b(garlic|onion|shallot|shallots|leek|leeks|scallion|scallions|spring onion|bean|beans|lentil|lentils|chickpea|chickpeas|apple|apples|pear|pears|mango|watermelon|honey|agave|mushroom|mushrooms|avocado|cauliflower)\b/i.test(
        ing
      ),
  },
  {
    id: 'nightshades',
    name: 'Nightshades',
    match: (ing: string) =>
      /\b(tomato|tomatoes|potato|potatoes|bell pepper|pepper|peppers|capsicum|chili|chilli|eggplant|aubergine|paprika|cayenne|tobacco|tomatillo)\b/i.test(
        ing
      ) && !/black pepper/i.test(ing),
  },
  {
    id: 'histamines',
    name: 'Histamines',
    match: (ing: string) =>
      /\b(spinach|vinegar|fermented|kombucha|wine|beer|alcohol|cured|bacon|salami|sausage|sauerkraut|kimchi|soy sauce)\b/i.test(
        ing
      ),
  },
  {
    id: 'other',
    name: 'Other Foods',
    match: () => true,
  },
];

interface SymptomPoint {
  id: string;
  timeLabel: string;
  dateStr: string;
  timestampMs: number;
  bloat: number;
  soreness: number;
  stool?: number | null;
}

interface ClinicalSection {
  title: string;
  paragraphs: string[];
  bullets: string[];
}

const WEEK_MS = 7 * 86_400_000;

function containsWord(haystack: string, word: string): boolean {
  if (!word) return false;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}(?:e?s)?\\b`, 'i').test(haystack);
}

function stripJsonFromReport(rawText: string): string {
  if (!rawText) return '';
  let text = rawText;
  text = text.replace(/```json[\s\S]*?```/gi, '');
  const unclosedJsonIdx = text.search(/```json/i);
  if (unclosedJsonIdx !== -1) {
    text = text.slice(0, unclosedJsonIdx);
  }
  const rawJsonIdx = text.search(/\{\s*"(health_score|suspected_triggers|id)"/i);
  if (rawJsonIdx !== -1) {
    text = text.slice(0, rawJsonIdx);
  }
  return text.trim();
}

function parseClinicalSections(rawText: string): ClinicalSection[] {
  if (!rawText) return [];
  const withoutJson = stripJsonFromReport(rawText);
  const lines = withoutJson.split('\n');
  const sections: ClinicalSection[] = [];
  let currentTitle = 'Executive Medical Summary';
  let currentParagraphs: string[] = [];
  let currentBullets: string[] = [];

  const flush = () => {
    if (currentParagraphs.length > 0 || currentBullets.length > 0) {
      sections.push({
        title: currentTitle.replace(/^#+\s*/, '').replace(/\*+/g, '').trim(),
        paragraphs: [...currentParagraphs],
        bullets: [...currentBullets],
      });
      currentParagraphs = [];
      currentBullets = [];
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith('```')) continue;

    if (
      line.startsWith('{') ||
      line.startsWith('}') ||
      line.startsWith(']') ||
      /^"(health_score|suspected_triggers|id|ingredient_name|confidence|lag_window|transit_zone|primary_symptoms|implicated_meals|mechanism|safe_foods|name|sessions_count|category)":/i.test(line)
    ) {
      continue;
    }

    if (/^#{2,4}\s+/.test(line)) {
      flush();
      currentTitle = line.replace(/^#{2,4}\s+/, '').replace(/\*+/g, '').trim();
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      currentBullets.push(line.replace(/^[-*]\s+/, '').trim());
    } else if (/^\d+\.\s+/.test(line)) {
      currentBullets.push(line.replace(/^\d+\.\s+/, '').trim());
    } else {
      currentParagraphs.push(line);
    }
  }
  flush();
  return sections;
}

function renderFormattedInline(text: string): React.ReactNode {
  const clean = text.replace(/```/g, '').trim();
  const parts = clean.split(/(\*\*.*?\*\*|`.*?`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={idx} className="font-bold text-slate-950 dark:text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <span
          key={idx}
          className="px-1 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 font-mono text-[11px] text-slate-900 dark:text-zinc-200"
        >
          {part.slice(1, -1)}
        </span>
      );
    }
    return part;
  });
}

function getSectionIcon(title: string) {
  const lower = title.toLowerCase();
  if (lower.includes('summary') || lower.includes('synthesis')) return Sparkles;
  if (lower.includes('trigger') || lower.includes('kinetics') || lower.includes('suspect')) return Flame;
  if (lower.includes('upper') || lower.includes('reflux') || lower.includes('oral')) return Activity;
  if (lower.includes('stool') || lower.includes('transit') || lower.includes('bowel')) return Droplets;
  if (lower.includes('safe') || lower.includes('baseline') || lower.includes('tolerated')) return ShieldCheck;
  if (lower.includes('plan') || lower.includes('recommend') || lower.includes('action')) return ClipboardList;
  return Activity;
}

export const InsightsView: React.FC<InsightsViewProps> = React.memo(({
  insights,
  meals,
  symptoms,
  exercises = [],
  user,
  isAiRunning,
  onReanalyze,
  onEditMeal,
  onEditSymptom,
  onLogMeal,
}) => {
  const [balanceTimeframe, setBalanceTimeframe] = useState<'day' | 'week'>('day');
  const [activeAnalyticsSymptomId, setActiveAnalyticsSymptomId] = useState<string | null>(null);
  const [card1Symptom, setCard1Symptom] = useState<string>(() => {
    try {
      return localStorage.getItem('digestlog_card_1') || 'bloating';
    } catch {
      return 'bloating';
    }
  });
  const [card2Symptom, setCard2Symptom] = useState<string>(() => {
    try {
      return localStorage.getItem('digestlog_card_2') || 'stomach_pain';
    } catch {
      return 'stomach_pain';
    }
  });
  const [selectedIngredient, setSelectedIngredient] = useState<string | null>(null);
  const [selectedMealPinId, setSelectedMealPinId] = useState<string | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<ChartTimeframe>('3days');
  const [timeframeOffset, setTimeframeOffset] = useState<number>(0);
  const [intoleranceTab, setIntoleranceTab] = useState<'all' | 'suspects' | 'safe' | 'crossref'>('all');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const [inspectingRegistryItem, setInspectingRegistryItem] = useState<LongitudinalIntoleranceItem | null>(null);
  const [isDoctorSummaryOpen, setIsDoctorSummaryOpen] = useState(false);
  const [selectedTimelinePoint, setSelectedTimelinePoint] = useState<SymptomPoint | null>(null);
  const [userCorrectionsVersion, setUserCorrectionsVersion] = useState(0);

  useEffect(() => {
    const handleCorrectionsUpdated = () => {
      setUserCorrectionsVersion((v) => v + 1);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('digestlog_user_corrections_updated', handleCorrectionsUpdated);
      return () => window.removeEventListener('digestlog_user_corrections_updated', handleCorrectionsUpdated);
    }
  }, []);

  const transitSpeed = user?.transitSpeed || inferTransitSpeedFromSymptoms(symptoms);
  const baselineSettings = useMemo(() => localStore.getBaselineSettings(), []);
  
  const longitudinalStats = useMemo(
    () => computeLongitudinalIntoleranceStats(meals, symptoms, baselineSettings, transitSpeed),
    [meals, symptoms, baselineSettings, transitSpeed, userCorrectionsVersion]
  );
  
  const fodmapSubtypeCorrelations = useMemo(
    () => computeFodmapSubtypeCorrelations(meals, symptoms, transitSpeed),
    [meals, symptoms, transitSpeed]
  );

  const safeRecoveryMeals = useMemo(
    () => generateSafeRecoveryMeals(meals, symptoms, transitSpeed),
    [meals, symptoms, transitSpeed]
  );

  const motilityKinetics = useMemo(
    () => computeMotilityKinetics(symptoms, meals, transitSpeed),
    [symptoms, meals, transitSpeed]
  );

  // Helper to determine if a tag/ingredient is present in a meal
  const isTagInMeal = (meal: Meal, tag: string) => {
    const targetTag = tag.toLowerCase().trim();

    if (meal.anti_aggravates) {
      const anti = meal.anti_aggravates.toLowerCase();
      if (
        ((targetTag === 'gluten' || targetTag === 'wheat') && anti.includes('gluten')) ||
        ((targetTag === 'dairy' || targetTag === 'lactose' || targetTag === 'milk') && anti.includes('dairy')) ||
        ((targetTag === 'allium' || targetTag === 'garlic' || targetTag === 'onion') && anti.includes('allium')) ||
        ((targetTag === 'nuts' || targetTag === 'peanuts') && anti.includes('nuts')) ||
        ((targetTag === 'eggs' || targetTag === 'egg') && anti.includes('eggs')) ||
        (targetTag === 'soy' && anti.includes('soy'))
      ) {
        return false;
      }
    }

    const extracted = extractFoodsFromMeal(meal).map((f) => f.toLowerCase().trim());
    if (extracted.includes(targetTag)) return true;
    // Whole-word match only, so "egg" finds "boiled eggs" but not "eggplant", and "rice" not "licorice".
    return extracted.some((f) => containsWord(f, targetTag) || containsWord(targetTag, f));
  };

  // Meals matching the selected registry ingredient
  const mealsForInspectingRegistryItem = useMemo(() => {
    if (!inspectingRegistryItem) return [];
    return meals
      .filter((meal) => isTagInMeal(meal, inspectingRegistryItem.tag))
      .sort((a, b) => new Date(b.timestamp_iso).getTime() - new Date(a.timestamp_iso).getTime());
  }, [inspectingRegistryItem, meals]);

  // Precompute meal counts per tag
  const tagMealCountMap = useMemo(() => {
    const countMap = new Map<string, number>();
    for (const meal of meals) {
      const foods = extractFoodsFromMeal(meal);
      const seenForMeal = new Set<string>();
      for (const f of foods) {
        const lower = f.toLowerCase().trim();
        if (!seenForMeal.has(lower)) {
          seenForMeal.add(lower);
          countMap.set(lower, (countMap.get(lower) || 0) + 1);
        }
      }
    }
    return countMap;
  }, [meals]);

  const getMealCountForTag = (tag: string) => {
    const lower = tag.toLowerCase().trim();
    const direct = tagMealCountMap.get(lower);
    if (direct !== undefined) return direct;
    return meals.filter((meal) => isTagInMeal(meal, tag)).length;
  };

  // Helper to analyze symptoms following a meal
  const getMealSymptomContext = (meal: Meal) => {
    const mTime = new Date(meal.timestamp_iso).getTime();
    const following = symptoms.filter((s) => {
      if (s.associated_meal_id) return s.associated_meal_id === meal.id;
      const sTime = new Date(s.timestamp_iso).getTime();
      const lag = (sTime - mTime) / (1000 * 60 * 60);
      return lag >= 0.5 && lag <= 36;
    });

    const spikes = following.filter((s) => isFlareSymptom(s));

    return {
      hasReaction: spikes.length > 0,
      symptomsCount: following.length,
      maxBloat: following.length > 0 ? Math.max(...following.map((s) => s.bloating_score)) : 0,
      maxSoreness: following.length > 0 ? Math.max(...following.map((s) => s.soreness_score)) : 0,
      firstSpike: spikes[0],
      firstLag: spikes[0] ? calculateLagHours(meal.timestamp_iso, spikes[0].timestamp_iso) : null,
    };
  };

  const activeInsight = insights[0];
  const clinicalSections = useMemo(
    () => parseClinicalSections(activeInsight?.ai_summary_markdown || ''),
    [activeInsight]
  );

  // Calibration and protocol tracking
  const daysLoggedCount = useMemo(() => {
    const dates = new Set<string>();
    meals.forEach((m) => dates.add(m.date));
    symptoms.forEach((s) => dates.add(s.date));
    return Math.max(1, dates.size);
  }, [meals, symptoms]);

  const isDiaryCalibrating = daysLoggedCount < 14;

  // Counts for summary pills
  const confirmedCount = useMemo(
    () => longitudinalStats.filter((s) => s.status === 'Confirmed Culprit (User Verified)' || s.status === 'Confirmed Culprit' || s.status === 'Persistent Culprit').length,
    [longitudinalStats]
  );
  const suspectCount = useMemo(
    () => longitudinalStats.filter((s) => s.status === 'Preliminary Suspect' || s.status === 'Emerging Suspect' || s.status === 'Eliminating').length,
    [longitudinalStats]
  );
  const safeCount = useMemo(
    () => longitudinalStats.filter((s) => s.status === 'Safe Baseline' || s.status === 'Tolerated' || s.status === 'Tolerated (User Verified)').length,
    [longitudinalStats]
  );
  const crossRefExoneratedCount = useMemo(
    () => longitudinalStats.filter((s) => s.status === 'Tolerated (Ruled Out by Cross-Ref)' || s.isExoneratedByCrossReference).length,
    [longitudinalStats]
  );
  const adverseTotalCount = useMemo(
    () => confirmedCount + suspectCount,
    [confirmedCount, suspectCount]
  );

  // Today's Gut Balance Rating
  const todayGutBalance = useMemo(() => {
    const todayStr = formatIsoDate(new Date());
    let targetSymptoms = symptoms.filter((s) => s.date === todayStr);
    let isToday = true;

    if (targetSymptoms.length === 0 && symptoms.length > 0) {
      const sorted = [...symptoms].sort(
        (a, b) => new Date(b.timestamp_iso).getTime() - new Date(a.timestamp_iso).getTime()
      );
      const latestDate = sorted[0].date;
      targetSymptoms = symptoms.filter((s) => s.date === latestDate);
      isToday = false;
    }

    if (targetSymptoms.length === 0) {
      return {
        ratio: 100,
        hasLogs: false,
        isToday: true,
        totalLogs: 0,
        flareCount: 0,
        calmCount: 0,
        avgBloat: 1.0,
        avgSoreness: 1.0,
        stoolOptimalPercent: 0,
        status: 'CALM' as const,
        description: 'No symptoms or digestive discomfort reported today. Log a check-in to track your health balance.',
      };
    }

    let calmCount = 0;
    let flareCount = 0;
    let totalScore = 0;
    let totalBloat = 0;
    let totalSoreness = 0;
    let stoolOptimalCount = 0;
    let stoolLoggedCount = 0;

    for (const s of targetSymptoms) {
      const bloat = s.bloating_score ?? 0;
      const soreness = s.soreness_score ?? 0;
      const stool = s.stool_type;

      totalBloat += bloat;
      totalSoreness += soreness;

      const isBadBloat = bloat >= 5;
      const isBadSoreness = soreness >= 5;
      const isBadStool = stool ? stool <= 2 || stool >= 6 : false;

      if (stool) {
        stoolLoggedCount++;
        if (stool === 3 || stool === 4) stoolOptimalCount++;
      }

      if (isBadBloat || isBadSoreness || isBadStool) {
        flareCount++;
      } else {
        calmCount++;
      }

      const bloatPenalty = (bloat / 10) * 45;
      const sorenessPenalty = (soreness / 10) * 45;
      let stoolPenalty = 0;
      if (stool) {
        if (stool === 3 || stool === 4) stoolPenalty = 0;
        else if (stool === 2 || stool === 5) stoolPenalty = 10;
        else stoolPenalty = 20;
      }

      const checkinRating = Math.max(10, Math.min(100, Math.round(100 - bloatPenalty - sorenessPenalty - stoolPenalty)));
      totalScore += checkinRating;
    }

    const todayRatio = Math.round(totalScore / targetSymptoms.length);
    const isStable = todayRatio >= 75 && flareCount === 0;

    let desc = '';
    if (flareCount === 0) {
      desc = `Digestion is calm across ${targetSymptoms.length} check-in${targetSymptoms.length > 1 ? 's' : ''} ${isToday ? 'today' : 'in latest logs'} with minimal bloating.`;
    } else {
      desc = `Recorded ${flareCount} discomfort spike${flareCount > 1 ? 's' : ''} across ${targetSymptoms.length} check-in${targetSymptoms.length > 1 ? 's' : ''} ${isToday ? 'today' : 'in latest logs'}.`;
    }

    return {
      ratio: todayRatio,
      hasLogs: true,
      isToday,
      totalLogs: targetSymptoms.length,
      flareCount,
      calmCount,
      avgBloat: Number((totalBloat / targetSymptoms.length).toFixed(1)),
      avgSoreness: Number((totalSoreness / targetSymptoms.length).toFixed(1)),
      stoolOptimalPercent: stoolLoggedCount > 0 ? Math.round((stoolOptimalCount / stoolLoggedCount) * 100) : 0,
      status: isStable ? ('STABLE' as const) : ('FLARING' as const),
      description: desc,
    };
  }, [symptoms]);

  // Week's Gut Balance Rating
  const weekGutBalance = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekSymptoms = symptoms.filter((s) => new Date(s.timestamp_iso) >= weekAgo);

    const datesMap = new Map<string, { total: number; flares: number; totalBloat: number; totalSoreness: number }>();
    let totalBloat = 0;
    let totalSoreness = 0;
    let stoolOptimalCount = 0;
    let stoolLoggedCount = 0;

    for (const s of weekSymptoms) {
      const d = s.date;
      const cur = datesMap.get(d) || { total: 0, flares: 0, totalBloat: 0, totalSoreness: 0 };
      cur.total++;
      const bloat = s.bloating_score ?? 0;
      const soreness = s.soreness_score ?? 0;
      cur.totalBloat += bloat;
      cur.totalSoreness += soreness;
      totalBloat += bloat;
      totalSoreness += soreness;

      const stool = s.stool_type;
      if (stool) {
        stoolLoggedCount++;
        if (stool === 3 || stool === 4) stoolOptimalCount++;
      }

      if (bloat >= 5 || soreness >= 5 || (stool ? stool <= 2 || stool >= 6 : false)) {
        cur.flares++;
      }
      datesMap.set(d, cur);
    }

    let stableDaysCount = 0;
    let flareDaysCount = 0;
    datesMap.forEach((v) => {
      if (v.flares === 0) stableDaysCount++;
      else flareDaysCount++;
    });

    let finalRatio = 80;
    if (activeInsight?.health_score && typeof activeInsight.health_score === 'number') {
      finalRatio = activeInsight.health_score;
    } else if (weekSymptoms.length > 0) {
      const totalChecks = weekSymptoms.length;
      const flareChecks = weekSymptoms.filter((s) => (s.bloating_score || 1) >= 3 || (s.soreness_score || 1) >= 3).length;
      finalRatio = Math.max(25, Math.min(100, Math.round(100 - (flareChecks / totalChecks) * 45)));
    }

    const isStable = finalRatio >= 75 && flareDaysCount <= 1;

    let desc = '';
    if (datesMap.size === 0) {
      desc = 'No check-ins recorded in the past 7 days. Log your daily meals and sensations to populate your weekly trend.';
    } else if (flareDaysCount === 0) {
      desc = `Excellent digestive equilibrium across ${datesMap.size} logged day${datesMap.size > 1 ? 's' : ''} this week without recorded flares.`;
    } else {
      desc = `Observed ${flareDaysCount} day${flareDaysCount > 1 ? 's' : ''} with discomfort spikes across ${datesMap.size} logged day${datesMap.size > 1 ? 's' : ''} this week.`;
    }

    return {
      ratio: finalRatio,
      hasLogs: datesMap.size > 0,
      totalDays: Math.max(1, datesMap.size),
      stableDaysCount,
      flareDaysCount,
      avgBloat: weekSymptoms.length > 0 ? Number((totalBloat / weekSymptoms.length).toFixed(1)) : 1.2,
      avgSoreness: weekSymptoms.length > 0 ? Number((totalSoreness / weekSymptoms.length).toFixed(1)) : 1.1,
      stoolOptimalPercent: stoolLoggedCount > 0 ? Math.round((stoolOptimalCount / stoolLoggedCount) * 100) : 0,
      status: isStable ? ('STABLE' as const) : ('FLARING' as const),
      description: desc,
    };
  }, [symptoms, activeInsight]);

  const currentGutBalance = balanceTimeframe === 'day' ? todayGutBalance : weekGutBalance;

  // ---- Insights v2 (Layout A, spec §8) ----
  const isV2 = activeInsight?.format_version === 2;
  const [openPage, setOpenPage] = useState<MoreAnalysisPage | null>(null);
  // The v2 gauge opens on the week view (the stored gut score); legacy keeps its own default.
  const [v2Timeframe, setV2Timeframe] = useState<'day' | 'week'>('week');
  const v2RootRef = useRef<HTMLDivElement>(null);
  const lastOpenedPage = useRef<MoreAnalysisPage | null>(null);
  const wasCovered = useRef(false);
  const coverOpen = isV2 && (openPage !== null || activeAnalyticsSymptomId !== null);

  // Week gauge: the insight's own gut score, with the reason line vs the 7 days before it was generated.
  const v2Gut = useMemo(() => {
    const g = isV2 ? activeInsight?.brief?.gutScore ?? null : null;
    if (!g || !activeInsight) return null;
    const genMs = new Date(activeInsight.generated_at_iso).getTime();
    const endMs = Number.isFinite(genMs) ? genMs : Date.now();
    return { score: g, reason: gutScoreReason(g, computeGutScore(meals, symptoms, endMs - WEEK_MS)) };
  }, [isV2, activeInsight, meals, symptoms]);

  // Verified findings are derived at read time (the cloud copy never carries them), filtered to the
  // engine's candidates, and the headline/summary re-guarded against what is actually shown.
  const v2View = useMemo<InsightView>(
    () => (isV2 && activeInsight ? deriveInsightView(activeInsight, meals, symptoms) : { ai: null, findings: [] }),
    [isV2, activeInsight, meals, symptoms]
  );

  // "Week of 8 Oct–15 Oct · updated 09:48": the 7 days ending at the insight's generated time.
  const v2Subtitle = useMemo(() => {
    if (!isV2 || !activeInsight) return '';
    const gen = new Date(activeInsight.generated_at_iso);
    if (Number.isNaN(gen.getTime())) return '';
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const time = gen.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `Week of ${fmt(new Date(gen.getTime() - WEEK_MS))}–${fmt(gen)} · updated ${time}`;
  }, [isV2, activeInsight]);

  // While an overlay is open, lock the page behind it (same body/html lock the app uses for its modals).
  // The main page is never scrolled, so its position is simply still there on close.
  useEffect(() => {
    if (!coverOpen) return;
    const body = document.body.style.overflow;
    const html = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = body;
      document.documentElement.style.overflow = html;
    };
  }, [coverOpen]);

  // Toggle `inert` on the main page and, when the last overlay closes, return focus to the More analysis
  // row that opened it. Both happen in this one layout effect, in this order, on purpose: an inert
  // subtree cannot take focus, so `inert` must be removed before `.focus()` runs. (A separate passive
  // useEffect for `inert` would run after this layout effect, making the focus call a silent no-op.)
  useLayoutEffect(() => {
    const root = v2RootRef.current;
    if (coverOpen) {
      root?.setAttribute('inert', '');
      wasCovered.current = true;
      return;
    }
    root?.removeAttribute('inert');
    if (!wasCovered.current) return;
    wasCovered.current = false;
    const page = lastOpenedPage.current;
    if (page) root?.querySelector<HTMLElement>(`[data-more-page="${page}"]`)?.focus({ preventScroll: true });
  }, [coverOpen]);

  const openSubPage = (page: MoreAnalysisPage) => {
    lastOpenedPage.current = page;
    setOpenPage(page);
  };

  // Timeframe window calculation
  const { periodStartMs, periodEndMs, timeframeRangeLabel } = useMemo(() => {
    const now = new Date();
    const currentEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();

    if (selectedTimeframe === 'day') {
      const endMs = currentEnd - timeframeOffset * 86400000;
      const startMs = endMs - 86400000 + 1;
      const d = new Date(startMs);
      const isToday = formatIsoDate(d) === formatIsoDate(now);
      const label = isToday ? 'Today' : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      return { periodStartMs: startMs, periodEndMs: endMs, timeframeRangeLabel: label };
    }

    if (selectedTimeframe === '3days') {
      const endMs = currentEnd - timeframeOffset * 3 * 86400000;
      const startMs = endMs - 3 * 86400000 + 1;
      const dStart = new Date(startMs);
      const dEnd = new Date(endMs);
      const label = `${dStart.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${dEnd.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
      return { periodStartMs: startMs, periodEndMs: endMs, timeframeRangeLabel: label };
    }

    if (selectedTimeframe === 'week') {
      const endMs = currentEnd - timeframeOffset * 7 * 86400000;
      const startMs = endMs - 7 * 86400000 + 1;
      const dStart = new Date(startMs);
      const dEnd = new Date(endMs);
      const label = `${dStart.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${dEnd.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
      return { periodStartMs: startMs, periodEndMs: endMs, timeframeRangeLabel: label };
    }

    // month
    const endMs = currentEnd - timeframeOffset * 30 * 86400000;
    const startMs = endMs - 30 * 86400000 + 1;
    const dStart = new Date(startMs);
    const dEnd = new Date(endMs);
    const label = `${dStart.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${dEnd.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
    return { periodStartMs: startMs, periodEndMs: endMs, timeframeRangeLabel: label };
  }, [selectedTimeframe, timeframeOffset]);

  // Continuous Symptom Points for Line Graph
  const timelinePoints = useMemo<SymptomPoint[]>(() => {
    let filtered = symptoms.filter((s) => {
      const t = new Date(s.timestamp_iso).getTime();
      return t >= periodStartMs && t <= periodEndMs;
    });

    if (filtered.length === 0 && timeframeOffset === 0 && symptoms.length > 0) {
      const sorted = [...symptoms].sort(
        (a, b) => new Date(b.timestamp_iso).getTime() - new Date(a.timestamp_iso).getTime()
      );
      const latestDate = sorted[0].date;
      filtered = symptoms.filter((s) => s.date === latestDate);
    }

    filtered.sort((a, b) => new Date(a.timestamp_iso).getTime() - new Date(b.timestamp_iso).getTime());

    return filtered.map((s) => {
      const d = new Date(s.timestamp_iso);
      const timeLabel = formatTime(s.timestamp_iso);
      return {
        id: s.id,
        timeLabel: selectedTimeframe === 'day' ? timeLabel : `${d.getDate()}/${d.getMonth() + 1} ${timeLabel}`,
        dateStr: s.date,
        timestampMs: d.getTime(),
        bloat: s.bloating_score || 1,
        soreness: s.soreness_score || 1,
        stool: s.stool_type,
      };
    });
  }, [symptoms, periodStartMs, periodEndMs, selectedTimeframe, timeframeOffset]);

  // Meals within timeframe
  const timeframeMeals = useMemo(() => {
    let targetMeals = meals.filter((m) => {
      const t = new Date(m.timestamp_iso).getTime();
      return t >= periodStartMs && t <= periodEndMs;
    });

    if (targetMeals.length === 0 && timeframeOffset === 0 && meals.length > 0) {
      const sorted = [...meals].sort(
        (a, b) => new Date(b.timestamp_iso).getTime() - new Date(a.timestamp_iso).getTime()
      );
      const latestDate = sorted[0].date;
      targetMeals = meals.filter((m) => m.date === latestDate);
    }

    return targetMeals.sort((a, b) => new Date(a.timestamp_iso).getTime() - new Date(b.timestamp_iso).getTime());
  }, [meals, periodStartMs, periodEndMs, timeframeOffset]);

  // SVG Chart Dimensions
  const chartWidth = 340;
  const chartHeight = 110;
  const plotHeight = 70;
  const paddingTop = 12;
  const paddingX = 24;

  const getX = (index: number, total: number) => {
    if (total <= 1) return chartWidth / 2;
    return paddingX + (index / (total - 1)) * (chartWidth - paddingX * 2);
  };

  const getY = (val: number, maxVal: number) => {
    const normalized = Math.max(0, Math.min(1, (val - 1) / (maxVal - 1)));
    return paddingTop + plotHeight - normalized * plotHeight;
  };

  const bloatPath = useMemo(() => {
    if (timelinePoints.length === 0) return '';
    return timelinePoints
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i, timelinePoints.length)} ${getY(p.bloat, 5)}`)
      .join(' ');
  }, [timelinePoints]);

  const bloatAreaPath = useMemo(() => {
    if (timelinePoints.length === 0) return '';
    const pointsStr = timelinePoints
      .map((p, i) => `L ${getX(i, timelinePoints.length)} ${getY(p.bloat, 5)}`)
      .join(' ');
    const firstX = getX(0, timelinePoints.length);
    const lastX = getX(timelinePoints.length - 1, timelinePoints.length);
    const bottomY = plotHeight + paddingTop;
    return `M ${firstX} ${bottomY} ${pointsStr} L ${lastX} ${bottomY} Z`;
  }, [timelinePoints]);

  const sorenessPath = useMemo(() => {
    if (timelinePoints.length === 0) return '';
    return timelinePoints
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i, timelinePoints.length)} ${getY(p.soreness, 5)}`)
      .join(' ');
  }, [timelinePoints]);

  // Meal Pins Calculation
  const mealPins = useMemo(() => {
    if (timelinePoints.length === 0 || timeframeMeals.length === 0) return [];
    const minT = timelinePoints[0].timestampMs;
    const maxT = timelinePoints[timelinePoints.length - 1].timestampMs;
    const isSingle = minT === maxT;

    return timeframeMeals.map((m) => {
      const mT = new Date(m.timestamp_iso).getTime();
      let x = chartWidth / 2;
      if (!isSingle && maxT > minT) {
        const norm = Math.max(0, Math.min(1, (mT - minT) / (maxT - minT)));
        x = paddingX + norm * (chartWidth - paddingX * 2);
      }
      return {
        id: m.id,
        meal: m,
        name: m.description || 'Meal',
        timeStr: formatTime(m.timestamp_iso),
        timestampMs: mT,
        x,
      };
    });
  }, [timelinePoints, timeframeMeals]);

  // 3. Intolerance Registry Items Filtered by Tab
  const [isExpandedRegistry, setIsExpandedRegistry] = useState(false);

  const registryItems = useMemo(() => {
    let items = [...longitudinalStats];

    if (intoleranceTab === 'suspects') {
      items = items.filter(
        (s) =>
          s.status === 'Confirmed Culprit (User Verified)' ||
          s.status === 'Confirmed Culprit' ||
          s.status === 'Persistent Culprit' ||
          s.status === 'Eliminating' ||
          s.status === 'Emerging Suspect' ||
          s.status === 'Preliminary Suspect'
      );
    } else if (intoleranceTab === 'safe') {
      items = items.filter(
        (s) =>
          s.status === 'Safe Baseline' ||
          s.status === 'Tolerated' ||
          s.status === 'Tolerated (User Verified)'
      );
    } else if (intoleranceTab === 'crossref') {
      items = items.filter(
        (s) => s.status === 'Tolerated (Ruled Out by Cross-Ref)' || s.isExoneratedByCrossReference
      );
    }

    return items;
  }, [longitudinalStats, intoleranceTab]);

  // Re-analysis handler
  const handleTriggerReanalysis = async () => {
    const lastRun = localStore.getLastAiRun();
    if (lastRun && Date.now() - lastRun < 45000) {
      const remaining = Math.ceil((45000 - (Date.now() - lastRun)) / 1000);
      setCooldownSeconds(remaining);
      const timer = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return;
    }

    await onReanalyze();
  };

  // Card renderers shared by the legacy layout and the v2 sub-pages.
  const renderTimelineCard = () => (
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs space-y-3">
        <div className="flex items-center justify-between pb-1.5 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Transit Kinetics
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Meal & Symptom Timeline
              </h2>
            </div>
          </div>

          {/* Timeframe selector pills */}
          {renderChartTimeframeToggle()}
        </div>

        {/* Date Window Navigation */}
        <div className="flex items-center justify-between text-xs px-1">
          <span className="font-bold text-slate-700 dark:text-zinc-300">
            {timeframeRangeLabel}
          </span>
          <div className="flex items-center space-x-1">
            <button
              type="button"
              onClick={() => {
                triggerHaptic('selection');
                setTimeframeOffset((prev) => prev + 1);
              }}
              className="p-1 rounded-lg bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 hover:bg-slate-200"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              disabled={timeframeOffset === 0}
              onClick={() => {
                triggerHaptic('selection');
                setTimeframeOffset((prev) => Math.max(0, prev - 1));
              }}
              className="p-1 rounded-lg bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 disabled:opacity-40"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Timeline SVG Chart Container */}
        {timelinePoints.length > 0 ? (
          <div className="relative w-full aspect-[340/115] bg-slate-50/60 dark:bg-zinc-900/40 rounded-2xl border border-slate-200/60 dark:border-zinc-800/80 overflow-hidden">
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-full overflow-visible">
              <defs>
                <linearGradient id="bloatGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Grid Lines */}
              <line x1={paddingX} y1={paddingTop + plotHeight} x2={chartWidth - paddingX} y2={paddingTop + plotHeight} stroke="currentColor" className="text-slate-200 dark:text-zinc-800" strokeWidth="1" strokeDasharray="3 3" />
              <line x1={paddingX} y1={paddingTop + plotHeight / 2} x2={chartWidth - paddingX} y2={paddingTop + plotHeight / 2} stroke="currentColor" className="text-slate-200 dark:text-zinc-800" strokeWidth="1" strokeDasharray="3 3" />

              {/* Bloat Area & Line */}
              {bloatAreaPath && <path d={bloatAreaPath} fill="url(#bloatGrad)" />}
              {bloatPath && <path d={bloatPath} fill="none" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

              {/* Soreness Line */}
              {sorenessPath && <path d={sorenessPath} fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4 2" strokeLinecap="round" strokeLinejoin="round" />}

              {/* Clickable Points */}
              {timelinePoints.map((p, idx) => {
                const cx = getX(idx, timelinePoints.length);
                const cy = getY(p.bloat, 5);
                const isSelected = selectedTimelinePoint?.id === p.id;
                return (
                  <g key={p.id} className="cursor-pointer" onClick={() => {
                    triggerHaptic('selection');
                    setSelectedTimelinePoint(isSelected ? null : p);
                  }}>
                    <circle cx={cx} cy={cy} r={isSelected ? 6 : 4} fill={p.bloat >= 4 ? '#EF4444' : '#3B82F6'} className="transition-all" />
                    {isSelected && <circle cx={cx} cy={cy} r="9" fill="none" stroke="#3B82F6" strokeWidth="2" className="animate-ping" />}
                  </g>
                );
              })}

              {/* Meal Pins */}
              {mealPins.map((mp) => (
                <g key={mp.id} transform={`translate(${mp.x - 7}, ${chartHeight - 16})`} className="cursor-pointer" onClick={() => setSelectedMealPinId(mp.id)}>
                  <circle cx="7" cy="7" r="7" className="fill-emerald-500/20 stroke-emerald-600 dark:stroke-emerald-400 stroke-1" />
                  <circle cx="7" cy="7" r="3" className="fill-emerald-600 dark:fill-emerald-400" />
                </g>
              ))}
            </svg>

            {/* Selected Point Popover */}
            {selectedTimelinePoint && (
              <div className="absolute top-2 right-2 p-2.5 rounded-xl bg-white/95 dark:bg-zinc-800/95 border border-slate-200 dark:border-zinc-700 shadow-md text-left text-[11px] space-y-0.5 pointer-events-auto">
                <div className="font-bold text-slate-900 dark:text-white">
                  {selectedTimelinePoint.timeLabel}
                </div>
                <div className="text-blue-600 dark:text-blue-400 font-semibold">
                  Bloat: {selectedTimelinePoint.bloat}/5 · Soreness: {selectedTimelinePoint.soreness}/5
                </div>
                {selectedTimelinePoint.stool && (
                  <div className="text-emerald-600 font-semibold">
                    Bristol Stool: Type {selectedTimelinePoint.stool}/7
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 text-center bg-slate-50 dark:bg-zinc-900/40 rounded-2xl border border-dashed border-slate-200 dark:border-zinc-800 text-xs text-slate-500">
            No check-in points recorded in this window. Log symptoms to see your transit curve.
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-zinc-400 pt-1">
          <div className="flex items-center space-x-3">
            <span className="flex items-center space-x-1">
              <span className="w-2.5 h-0.5 bg-blue-500 rounded-full inline-block" />
              <span>Bloating</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-2.5 h-0.5 bg-amber-500 rounded-full inline-block" />
              <span>Soreness</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
              <span>Logged Meals ({mealPins.length})</span>
            </span>
          </div>
        </div>
      </div>
  );

  const renderSymptomSquares = () => (
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
            Insights & Analytics
          </span>
          <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400">
            Tap card for trend & triggers
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SymptomSummaryCard
            symptomId={card1Symptom}
            symptoms={symptoms}
            onOpen={() => setActiveAnalyticsSymptomId(card1Symptom)}
            onSelectSymptom={(sym) => {
              setCard1Symptom(sym);
              try {
                localStorage.setItem('digestlog_card_1', sym);
              } catch {}
            }}
          />
          <SymptomSummaryCard
            symptomId={card2Symptom}
            symptoms={symptoms}
            onOpen={() => setActiveAnalyticsSymptomId(card2Symptom)}
            onSelectSymptom={(sym) => {
              setCard2Symptom(sym);
              try {
                localStorage.setItem('digestlog_card_2', sym);
              } catch {}
            }}
          />
        </div>
      </div>
  );

  const renderBowelCard = () => (
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs space-y-3.5">
        <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-cyan-50 dark:bg-cyan-950/40 text-cyan-600 dark:text-cyan-400">
              <Droplets className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Transit & Motility
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Bowel Movement Kinetics
              </h2>
            </div>
          </div>

          {/* Status Badge */}
          {(() => {
            const status = motilityKinetics.currentStatus;
            const badgeStyle =
              status === 'Regular Daily Rhythm'
                ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/30'
                : status === 'Delayed Evacuation'
                ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-500/30'
                : status === 'Active Colonic Stagnation'
                ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-500/30'
                : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 border-slate-200 dark:border-zinc-700';

            return (
              <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${badgeStyle}`}>
                {status}
              </span>
            );
          })()}
        </div>

        {/* 3 Core Metric Panels */}
        <div className="grid grid-cols-3 gap-2">
          {/* Panel 1: Last BM / Hours Since */}
          <div className="p-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/60 dark:border-zinc-800/80 text-left">
            <span className="text-[9.5px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider block mb-0.5">
              Last BM
            </span>
            <span className="text-base font-black text-slate-950 dark:text-white leading-tight block">
              {motilityKinetics.hoursSinceLastBowelMovement !== null
                ? `${motilityKinetics.hoursSinceLastBowelMovement}h ago`
                : 'No logs'}
            </span>
            <span className="text-[9px] text-slate-400 dark:text-zinc-500 font-medium block truncate mt-0.5">
              {motilityKinetics.lastBowelMovementBristol ? `Type ${motilityKinetics.lastBowelMovementBristol}/7 Bristol` : `${motilityKinetics.evacuationRhythmPerDay} BM/day`}
            </span>
          </div>

          {/* Panel 2: Bloat with BM vs Bloat during Gap */}
          <div className="p-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/60 dark:border-zinc-800/80 text-left">
            <span className="text-[9.5px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider block mb-0.5">
              Gap vs BM Bloat
            </span>
            <div className="flex items-baseline space-x-1">
              <span className="text-base font-black text-rose-600 dark:text-rose-400 leading-tight">
                {motilityKinetics.avgBloatOnGapDays}
              </span>
              <span className="text-[10px] text-slate-400">vs</span>
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                {motilityKinetics.avgBloatOnEvacuationDays}
              </span>
            </div>
            <span className="text-[9px] text-slate-500 dark:text-zinc-400 font-medium block truncate mt-0.5">
              {motilityKinetics.bloatDeltaOnGapDays > 0 ? `+${motilityKinetics.bloatDeltaOnGapDays} pts during gaps` : 'Calm balance'}
            </span>
          </div>

          {/* Panel 3: Post-BM Relief Drop */}
          <div className="p-2.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/60 dark:border-zinc-800/80 text-left">
            <span className="text-[9.5px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider block mb-0.5">
              Post-BM Relief
            </span>
            <span className="text-base font-black text-emerald-600 dark:text-emerald-400 leading-tight block">
              {motilityKinetics.postEvacuationReliefAverage < 0 ? `${motilityKinetics.postEvacuationReliefAverage} pts` : 'Stable'}
            </span>
            <span className="text-[9px] text-slate-400 dark:text-zinc-500 font-medium block truncate mt-0.5">
              Avg drop within 4h
            </span>
          </div>
        </div>

        {/* Motility Stagnation Insight Note */}
        <div className="p-3 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 text-left">
          <div className="flex items-start space-x-2">
            <Info className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-cyan-900 dark:text-cyan-200 leading-relaxed font-medium">
              {motilityKinetics.motilityDrivenBloatPercent >= 30
                ? `💡 ${motilityKinetics.motilityDrivenBloatPercent}% of your elevated bloating spikes occurred during missed bowel movement gaps (>24h). Colonic stagnation and trapped gas are primary drivers of distension; keeping daily motility regular protects safe foods from false alarms.`
                : motilityKinetics.clinicalRecommendation}
            </p>
          </div>
        </div>

        {/* Bristol Stool Consistency Ratio */}
        <div className="pt-1">
          <div className="flex items-center justify-between text-[10px] text-slate-400 dark:text-zinc-500 font-bold mb-1.5">
            <span>Stool Quality Distribution</span>
            <span>
              {motilityKinetics.bristolDistribution.optimalPercent}% Optimal (Type 3–4)
            </span>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden flex bg-slate-100 dark:bg-zinc-800">
            <div
              className="h-full bg-amber-500"
              style={{ width: `${motilityKinetics.bristolDistribution.hardConstipationPercent}%` }}
              title="Type 1-2 (Hard/Constipation)"
            />
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${motilityKinetics.bristolDistribution.optimalPercent}%` }}
              title="Type 3-4 (Optimal)"
            />
            <div
              className="h-full bg-cyan-500"
              style={{ width: `${motilityKinetics.bristolDistribution.looseUrgencyPercent}%` }}
              title="Type 5-7 (Loose/Rapid)"
            />
          </div>
          <div className="flex items-center justify-between text-[9px] text-slate-400 dark:text-zinc-500 mt-1 font-medium">
            <span className="flex items-center space-x-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
              <span>Hard ({motilityKinetics.bristolDistribution.hardConstipationPercent}%)</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
              <span>Optimal ({motilityKinetics.bristolDistribution.optimalPercent}%)</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 inline-block" />
              <span>Loose ({motilityKinetics.bristolDistribution.looseUrgencyPercent}%)</span>
            </span>
          </div>
        </div>
      </div>
  );

  const renderRegistryCard = (showSubtypeMatrix: boolean) => (
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center justify-between pb-1">
          <div className="flex items-center space-x-1.5">
            <Flame className="w-4 h-4 text-rose-500" />
            <h2 className="text-xs font-black text-slate-950 dark:text-white uppercase tracking-tight">
              LONGITUDINAL INTOLERANCE REGISTRY
            </h2>
          </div>
          <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400">
            Cross-Referenced
          </span>
        </div>
        <p className="text-xs text-slate-500 dark:text-zinc-400 mb-3.5">
          Differential cross-referencing compares calm days against flare episodes within precise biological windows (Dairy: 0.5–3h, FODMAPs: 4–24h) to isolate true triggers.
        </p>

        {/* 6-FODMAP Chemical Subtype Matrix */}
        {showSubtypeMatrix && (
        <div className="p-3.5 rounded-2xl bg-white dark:bg-zinc-900/60 border border-slate-200 dark:border-zinc-800/80 mb-3.5 shadow-2xs">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <span className="text-xs font-black text-slate-950 dark:text-white uppercase tracking-tight">
              6-FODMAP Subtype Matrix
            </span>
            <span className="text-[10px] text-slate-400 dark:text-zinc-500 font-mono">
              Monash Tested
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fodmapSubtypeCorrelations.map((sub) => {
              const isSuspect = sub.status === 'Suspected Trigger';
              const isTolerated = sub.status === 'Tolerated / Ruled Out';
              const cardBg = isSuspect
                ? 'bg-rose-50/60 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/50'
                : isTolerated
                ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/50'
                : 'bg-slate-50 dark:bg-zinc-950/40 border-slate-200/80 dark:border-zinc-800/60';

              const statusBadge = isSuspect
                ? 'bg-rose-100 dark:bg-rose-900/60 text-rose-800 dark:text-rose-200 border-rose-300 dark:border-rose-700'
                : isTolerated
                ? 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700'
                : 'bg-slate-200/70 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border-slate-300 dark:border-zinc-700';

              return (
                <div key={sub.subtype} className={`p-2.5 rounded-xl border ${cardBg} flex flex-col justify-between space-y-1.5`}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                      <span>{sub.emoji}</span>
                      <span>{sub.subtype}</span>
                    </span>
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded border ${statusBadge}`}>
                      {sub.status === 'Suspected Trigger' ? 'Suspect' : sub.status === 'Tolerated / Ruled Out' ? 'Tolerated' : 'Calibrating'}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-[10px] font-mono text-slate-600 dark:text-zinc-400">
                      <span>{sub.totalMealsCount} exposures</span>
                      <span className="font-bold">{sub.flareRatePercent}% flare rate</span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          isSuspect ? 'bg-rose-500' : isTolerated ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-zinc-600'
                        }`}
                        style={{ width: `${Math.max(5, Math.min(100, sub.flareRatePercent))}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* 4 Status Summary Badges */}
        <div className="grid grid-cols-4 gap-1.5 mb-3.5">
          <div className="py-2 px-1 rounded-2xl bg-rose-50/70 dark:bg-rose-950/20 border border-rose-200/80 dark:border-rose-900/40 text-center">
            <span className="block text-lg font-black text-rose-600 dark:text-rose-400">
              {confirmedCount}
            </span>
            <span className="text-[8.5px] font-black uppercase tracking-tight text-rose-700 dark:text-rose-300 block truncate">
              Confirmed
            </span>
          </div>

          <div className="py-2 px-1 rounded-2xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-900/40 text-center">
            <span className="block text-lg font-black text-amber-600 dark:text-amber-400">
              {suspectCount}
            </span>
            <span className="text-[8.5px] font-black uppercase tracking-tight text-amber-700 dark:text-amber-300 block truncate">
              Suspects
            </span>
          </div>

          <div className="py-2 px-1 rounded-2xl bg-teal-50/70 dark:bg-teal-950/20 border border-teal-200/80 dark:border-teal-900/40 text-center">
            <span className="block text-lg font-black text-teal-600 dark:text-teal-400">
              {crossRefExoneratedCount}
            </span>
            <span className="text-[8.5px] font-black uppercase tracking-tight text-teal-700 dark:text-teal-300 block truncate">
              Ruled Out
            </span>
          </div>

          <div className="py-2 px-1 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-900/40 text-center">
            <span className="block text-lg font-black text-emerald-600 dark:text-emerald-400">
              {safeCount}
            </span>
            <span className="text-[8.5px] font-black uppercase tracking-tight text-emerald-700 dark:text-emerald-300 block truncate">
              Safe
            </span>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="grid grid-cols-4 gap-1 p-1 bg-slate-100 dark:bg-zinc-900 rounded-2xl border border-slate-200/80 dark:border-zinc-800 mb-3">
          {(
            [
              { id: 'all', label: `All (${longitudinalStats.length})` },
              { id: 'suspects', label: `Suspects (${adverseTotalCount})` },
              { id: 'crossref', label: `Ruled Out (${crossRefExoneratedCount})` },
              { id: 'safe', label: `Safe (${safeCount})` },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                triggerHaptic('selection');
                setIntoleranceTab(tab.id);
                setIsExpandedRegistry(false);
              }}
              className={`py-1.5 rounded-xl text-[9.5px] font-black uppercase tracking-wider transition-all text-center truncate ${
                intoleranceTab === tab.id
                  ? 'bg-white dark:bg-zinc-800 text-slate-950 dark:text-white shadow-xs'
                  : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Items List */}
        {registryItems.length > 0 ? (
          <div className="space-y-2.5">
            {(isExpandedRegistry ? registryItems : registryItems.slice(0, 5)).map((item) => {
              const isConfirmed = item.status === 'Confirmed Culprit (User Verified)' || item.status === 'Confirmed Culprit' || item.status === 'Persistent Culprit';
              const isEliminating = item.status === 'Eliminating';
              const isSuspect = item.status === 'Emerging Suspect' || item.status === 'Preliminary Suspect';
              const isCrossrefExonerated = item.status === 'Tolerated (Ruled Out by Cross-Ref)' || item.isExoneratedByCrossReference;
              const isSafe = item.status === 'Safe Baseline' || item.status === 'Tolerated' || item.status === 'Tolerated (User Verified)';
              const isObserving = item.status === 'Observing (Gathering Data)';

              const badgeClass = isConfirmed
                ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-500/30'
                : isEliminating
                ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-500/30'
                : isSuspect
                ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30'
                : isCrossrefExonerated
                ? 'bg-teal-100 dark:bg-teal-500/20 text-teal-800 dark:text-teal-300 border border-teal-300 dark:border-teal-500/30'
                : isSafe
                ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30'
                : isObserving
                ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-500/30'
                : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 border border-slate-200 dark:border-zinc-700';

              const mealCount = getMealCountForTag(item.tag);

              return (
                <div
                  key={item.tag}
                  onClick={() => {
                    triggerHaptic('selection');
                    setInspectingRegistryItem(item);
                  }}
                  className={`p-3.5 rounded-3xl border transition-all cursor-pointer hover:shadow-md hover:border-slate-300 dark:hover:border-zinc-700 active:scale-[0.99] group ${
                    isConfirmed
                      ? 'bg-rose-50/30 dark:bg-rose-950/10 border-rose-200/80 dark:border-rose-900/40'
                      : isEliminating
                      ? 'bg-purple-50/30 dark:bg-purple-950/10 border-purple-200/80 dark:border-purple-900/40'
                      : isSuspect
                      ? 'bg-amber-50/30 dark:bg-amber-950/10 border-amber-200/80 dark:border-amber-900/40'
                      : isCrossrefExonerated
                      ? 'bg-teal-50/25 dark:bg-teal-950/10 border-teal-200/70 dark:border-teal-900/30'
                      : isSafe
                      ? 'bg-emerald-50/20 dark:bg-emerald-950/10 border-emerald-200/70 dark:border-emerald-900/30'
                      : 'bg-white dark:bg-zinc-900/40 border-slate-200/60 dark:border-zinc-800/80'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm font-bold text-slate-950 dark:text-white capitalize group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                        {item.tag}
                      </h3>
                      <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300">
                        {item.category || 'General'} · {item.expectedLagLabel || '2–24h'}
                      </span>
                      {item.isUserVerified && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/40">
                          User Verified
                        </span>
                      )}
                    </div>

                    <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${badgeClass}`}>
                      {item.status}
                    </span>
                  </div>

                  {/* Cross-Reference Exoneration Note */}
                  {item.crossReferenceNote && (
                    <p className="text-[11px] text-teal-800 dark:text-teal-300 font-medium mb-1.5 bg-teal-500/10 p-2 rounded-xl border border-teal-500/20">
                      💡 {item.crossReferenceNote}
                    </p>
                  )}

                  {/* Swap Suggestion */}
                  {item.swapSuggestion && (isConfirmed || isSuspect) && (
                    <div className="flex items-center space-x-1.5 text-[10px] text-emerald-800 dark:text-emerald-300 font-bold bg-emerald-500/10 px-2 py-1 rounded-xl mb-1.5 border border-emerald-500/20">
                      <Sparkles className="w-3 h-3 text-emerald-600 shrink-0" />
                      <span>Certified Swap: {item.swapSuggestion}</span>
                    </div>
                  )}

                  {/* Logs & Reaction Progress */}
                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mb-1">
                    <span>
                      Eaten {item.totalTimesEaten}x · Reaction in {item.adverseReactionCount} logs
                    </span>
                    <div className="flex items-center space-x-1.5 font-bold text-slate-900 dark:text-white">
                      <span>{item.reactionRate}% reaction</span>
                      {item.relativeRisk !== undefined && item.totalTimesEaten >= 4 && (
                        <span className={`text-[9.5px] px-1.5 py-0.5 rounded-md ${
                          item.relativeRisk >= 1.5
                            ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300'
                            : item.relativeRisk <= 1.15
                            ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300'
                            : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400'
                        }`}>
                          RR {item.relativeRisk}x
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-full bg-slate-100 dark:bg-zinc-800 h-2 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isConfirmed
                          ? 'bg-rose-500'
                          : isEliminating
                          ? 'bg-purple-500'
                          : isSuspect
                          ? 'bg-amber-500'
                          : isCrossrefExonerated
                          ? 'bg-teal-500'
                          : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(100, Math.max(5, item.reactionRate))}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-[11px] text-slate-400 dark:text-zinc-500 font-medium">
                    <span>
                      {item.averageLagHours > 0
                        ? `Avg onset: ~${item.averageLagHours}h (${getTransitPhaseLabel(item.averageLagHours, transitSpeed)})`
                        : 'Well tolerated'}
                    </span>
                    <span className="inline-flex items-center space-x-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-lg group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/60 transition-colors">
                      <span>View {mealCount} meals</span>
                      <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </div>
              );
            })}

            {registryItems.length > 5 && (
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('light');
                  setIsExpandedRegistry(!isExpandedRegistry);
                }}
                className="w-full py-2 rounded-xl bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-[11px] font-bold text-slate-700 dark:text-zinc-300 transition-all text-center mt-1"
              >
                {isExpandedRegistry ? 'Show Fewer' : `Show All ${registryItems.length} Evaluated Foods`}
              </button>
            )}
          </div>
        ) : (
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-zinc-900/40 border border-dashed border-slate-200 dark:border-zinc-800/80 text-center">
            <Info className="w-5 h-5 mx-auto mb-1 text-slate-400" />
            <p className="text-xs font-bold text-slate-800 dark:text-zinc-200">
              No items in this category
            </p>
          </div>
        )}
      </div>
  );

  const renderSafeMealsCard = () => (
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs space-y-3">
        <div className="flex items-center justify-between pb-1">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Decision Support
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Safe Recovery Meals
              </h2>
            </div>
          </div>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40">
            Zero-Flare Recipes
          </span>
        </div>
        <p className="text-xs text-slate-500 dark:text-zinc-400">
          Personalized gentle plates combining your verified Safe Baseline foods with certified Monash low-FODMAP ingredients.
        </p>

        <div className="space-y-2.5">
          {safeRecoveryMeals.map((rec) => (
            <div
              key={rec.id}
              className="p-3.5 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/70 dark:border-zinc-800 space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                  {rec.category}
                </span>
                <span className="text-[10px] text-slate-400 font-medium">
                  {rec.safeReason.slice(0, 42)}...
                </span>
              </div>

              <h4 className="text-xs font-bold text-slate-950 dark:text-white">
                {rec.title}
              </h4>
              <p className="text-[11px] text-slate-600 dark:text-zinc-300 leading-snug">
                {rec.description}
              </p>

              <div className="flex flex-wrap gap-1 pt-1">
                {rec.ingredients.map((ing, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 rounded-lg bg-white dark:bg-zinc-800 text-[10px] font-semibold text-slate-700 dark:text-zinc-300 border border-slate-200/60 dark:border-zinc-700"
                  >
                    ✓ {ing}
                  </span>
                ))}
              </div>

              {onLogMeal && (
                <button
                  type="button"
                  onClick={() => onLogMeal({ name: rec.title, ingredients: rec.ingredients })}
                  className="w-full mt-1 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center justify-center space-x-1.5 shadow-sm active:scale-98 transition-all"
                >
                  <Utensils className="w-3.5 h-3.5" />
                  <span>Log This Recovery Meal</span>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
  );

  const renderClinicalReport = (emptyText?: string) => (
    <>
        {clinicalSections.length > 0 ? (
          <div className="space-y-3">
            {clinicalSections.map((sec, idx) => {
              const IconComp = getSectionIcon(sec.title);
              return (
                <div
                  key={idx}
                  className="p-4 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/60 dark:border-zinc-800/80 text-left"
                >
                  <div className="flex items-center space-x-2 mb-2 pb-1.5 border-b border-slate-200/60 dark:border-zinc-800">
                    <div className="p-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
                      <IconComp className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="text-xs font-black uppercase tracking-wider text-slate-950 dark:text-white">
                      {sec.title}
                    </h3>
                  </div>

                  {sec.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-xs text-slate-600 dark:text-zinc-300 leading-relaxed font-medium mb-2 last:mb-0"
                    >
                      {renderFormattedInline(para)}
                    </p>
                  ))}

                  {sec.bullets.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {sec.bullets.map((b, bIdx) => (
                        <div
                          key={bIdx}
                          className="flex items-start space-x-2 text-xs text-slate-600 dark:text-zinc-300 leading-relaxed font-medium"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 dark:bg-indigo-400 mt-1.5 shrink-0" />
                          <span className="flex-1">{renderFormattedInline(b)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed text-left">
            {emptyText ??
              'Correlation kinetics track your gastrointestinal transit. When delayed distension occurs 6–14 hours postprandial, it signifies fermentation within the lower bowel rather than gastric irritation. Tap Refresh to run an AI clinical analysis.'}
          </div>
        )}
    </>
  );

  const renderOverlays = () => (
    <>
      {/* ========================================================================= */}
      {/* MODAL: INGREDIENT MEAL LOGS INSPECTION & EDITING (Portal)                 */}
      {/* ========================================================================= */}
      {inspectingRegistryItem && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 pointer-events-auto bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-200">
          <div
            className="fixed inset-0"
            onClick={() => {
              triggerHaptic('light');
              setInspectingRegistryItem(null);
            }}
          />

          <div className="relative w-full max-w-lg max-h-[85vh] bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800 rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)] dark:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.85)] flex flex-col z-10 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-zinc-800 flex items-start justify-between gap-3 bg-slate-50/50 dark:bg-zinc-900/30">
              <div>
                <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                  <h3 className="text-base font-black text-slate-950 dark:text-white capitalize">
                    {inspectingRegistryItem.tag}
                  </h3>
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-200/80 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300">
                    {inspectingRegistryItem.category || 'General'} · {inspectingRegistryItem.expectedLagLabel || '2–24h lag'}
                  </span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      inspectingRegistryItem.status === 'Confirmed Culprit (User Verified)' || inspectingRegistryItem.status === 'Confirmed Culprit' || inspectingRegistryItem.status === 'Persistent Culprit'
                        ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-500/30'
                        : inspectingRegistryItem.status === 'Emerging Suspect' || inspectingRegistryItem.status === 'Preliminary Suspect'
                        ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30'
                        : inspectingRegistryItem.status === 'Tolerated (Ruled Out by Cross-Ref)'
                        ? 'bg-teal-100 dark:bg-teal-500/20 text-teal-800 dark:text-teal-300 border border-teal-300 dark:border-teal-500/30'
                        : inspectingRegistryItem.status === 'Observing (Gathering Data)'
                        ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-500/30'
                        : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30'
                    }`}
                  >
                    {inspectingRegistryItem.status}
                  </span>
                </div>
                {inspectingRegistryItem.crossReferenceNote ? (
                  <p className="text-xs text-teal-800 dark:text-teal-300 font-semibold mt-1">
                    💡 {inspectingRegistryItem.crossReferenceNote}
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-zinc-400">
                    Logged in <span className="font-bold text-slate-900 dark:text-white">{mealsForInspectingRegistryItem.length} meals</span>. Tap <span className="font-semibold text-emerald-600 dark:text-emerald-400">Edit Meal</span> below to fix typos or adjust ingredients.
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  triggerHaptic('light');
                  setInspectingRegistryItem(null);
                }}
                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Interactive User Correction & Learning Toolbar */}
            <div className="px-4 sm:px-5 py-3 bg-slate-50/80 dark:bg-zinc-900/60 border-b border-slate-100 dark:border-zinc-800 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-wider flex items-center space-x-1">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span>Teach the Engine (Correction)</span>
                </span>
                {inspectingRegistryItem.isUserVerified && (
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      localStore.removeUserFoodCorrection(inspectingRegistryItem.tag);
                      setInspectingRegistryItem(null);
                    }}
                    className="text-[10px] text-rose-600 dark:text-rose-400 font-bold hover:underline cursor-pointer"
                  >
                    Reset Override
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('success');
                    localStore.saveUserFoodCorrection({
                      foodTag: inspectingRegistryItem.tag,
                      action: 'mark_tolerated',
                      createdAtIso: new Date().toISOString(),
                    });
                    setInspectingRegistryItem(null);
                  }}
                  className={`px-2 py-1.5 rounded-xl text-[11px] font-bold border transition-all text-center cursor-pointer ${
                    inspectingRegistryItem.status === 'Tolerated (User Verified)'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : 'bg-white dark:bg-zinc-800 text-emerald-700 dark:text-emerald-400 border-emerald-300/80 dark:border-emerald-700/60 hover:bg-emerald-50 dark:hover:bg-emerald-950/30'
                  }`}
                >
                  ✓ Safe for Me
                </button>

                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('warning');
                    localStore.saveUserFoodCorrection({
                      foodTag: inspectingRegistryItem.tag,
                      action: 'confirm_trigger',
                      createdAtIso: new Date().toISOString(),
                    });
                    setInspectingRegistryItem(null);
                  }}
                  className={`px-2 py-1.5 rounded-xl text-[11px] font-bold border transition-all text-center cursor-pointer ${
                    inspectingRegistryItem.status === 'Confirmed Culprit (User Verified)'
                      ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                      : 'bg-white dark:bg-zinc-800 text-rose-700 dark:text-rose-400 border-rose-300/80 dark:border-rose-700/60 hover:bg-rose-50 dark:hover:bg-rose-950/30'
                  }`}
                >
                  ⚠ Known Trigger
                </button>

                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    localStore.saveUserFoodCorrection({
                      foodTag: inspectingRegistryItem.tag,
                      action: 'blacklist_not_food',
                      createdAtIso: new Date().toISOString(),
                    });
                    setInspectingRegistryItem(null);
                  }}
                  className="px-2 py-1.5 rounded-xl text-[11px] font-bold border bg-white dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border-slate-300/80 dark:border-zinc-700 hover:bg-slate-100 dark:hover:bg-zinc-700 transition-all text-center cursor-pointer"
                  title="Remove modifier or non-ingredient"
                >
                  ✕ Not a Food
                </button>

                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('light');
                    const targetAlias = window.prompt(`Rename or alias "${inspectingRegistryItem.tag}" to:`, inspectingRegistryItem.tag);
                    if (targetAlias && targetAlias.trim() && targetAlias.trim().toLowerCase() !== inspectingRegistryItem.tag.toLowerCase()) {
                      localStore.saveUserFoodCorrection({
                        foodTag: inspectingRegistryItem.tag,
                        action: 'alias',
                        targetAlias: targetAlias.trim(),
                        createdAtIso: new Date().toISOString(),
                      });
                      setInspectingRegistryItem(null);
                    }
                  }}
                  className="px-2 py-1.5 rounded-xl text-[11px] font-bold border bg-white dark:bg-zinc-800 text-indigo-700 dark:text-indigo-400 border-indigo-300/80 dark:border-indigo-700/60 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all text-center cursor-pointer"
                >
                  ✎ Alias / Rename
                </button>
              </div>
            </div>

            {/* Scrollable Meal Logs List */}
            <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-3.5 custom-scrollbar">
              {mealsForInspectingRegistryItem.length > 0 ? (
                mealsForInspectingRegistryItem.map((meal) => {
                  const symptomCtx = getMealSymptomContext(meal);
                  const MealIcon = meal.meal_type === 'Drink' ? Coffee : Utensils;

                  const badgeColor =
                    meal.meal_type === 'Breakfast'
                      ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-500/30'
                      : meal.meal_type === 'Lunch'
                      ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/30'
                      : meal.meal_type === 'Dinner'
                      ? 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-800 dark:text-indigo-300 border-indigo-300 dark:border-indigo-500/30'
                      : meal.meal_type === 'Drink'
                      ? 'bg-cyan-100 dark:bg-cyan-500/15 text-cyan-800 dark:text-cyan-300 border-cyan-300 dark:border-cyan-500/30'
                      : 'bg-purple-100 dark:bg-purple-500/15 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-500/30';

                  const rawTags = meal.ingredients_tags
                    ? meal.ingredients_tags.split(',').map((t) => t.trim()).filter(Boolean)
                    : extractFoodsFromMeal(meal);

                  return (
                    <div
                      key={meal.id}
                      className="p-3.5 rounded-2xl bg-slate-50/90 dark:bg-zinc-900/60 border border-slate-200/80 dark:border-zinc-800/80 hover:border-slate-300 dark:hover:border-zinc-700 transition-all flex flex-col gap-2.5 shadow-xs"
                    >
                      <div className="flex items-center justify-between flex-wrap gap-1.5">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center space-x-1 text-[10px] font-bold px-2 py-0.5 rounded-md border ${badgeColor}`}>
                            <MealIcon className="w-3 h-3" />
                            <span>{meal.meal_type}</span>
                          </span>
                          <span className="text-[11px] font-semibold text-slate-500 dark:text-zinc-400">
                            {formatFullDate(meal.timestamp_iso)} · {formatTime(meal.timestamp_iso)}
                          </span>
                        </div>

                        {symptomCtx.hasReaction ? (
                          <span className="inline-flex items-center space-x-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-800/40">
                            <Flame className="w-2.5 h-2.5 text-rose-500" />
                            <span>
                              Flare Spike {symptomCtx.firstLag !== null ? `(${symptomCtx.firstLag}h later)` : ''}
                            </span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400">
                            <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" />
                            <span>No flare recorded</span>
                          </span>
                        )}
                      </div>

                      <p className="text-xs font-bold text-slate-900 dark:text-white leading-snug">
                        {meal.description || 'Meal Entry'}
                      </p>

                      {rawTags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {rawTags.map((tag, idx) => {
                            const isMatch =
                              tag.toLowerCase() === inspectingRegistryItem.tag.toLowerCase() ||
                              tag.toLowerCase().includes(inspectingRegistryItem.tag.toLowerCase()) ||
                              inspectingRegistryItem.tag.toLowerCase().includes(tag.toLowerCase());

                            return (
                              <span
                                key={idx}
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-md transition-all ${
                                  isMatch
                                    ? 'bg-amber-400/20 dark:bg-amber-400/20 text-amber-900 dark:text-amber-200 border border-amber-400/60 dark:border-amber-400/40 ring-1 ring-amber-400/40 font-bold'
                                    : 'bg-white dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 border border-slate-200/60 dark:border-zinc-700/60'
                                }`}
                              >
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {meal.anti_aggravates && (
                        <div className="flex flex-wrap gap-1">
                          {meal.anti_aggravates
                            .split(',')
                            .map((a) => a.trim())
                            .filter(Boolean)
                            .map((anti, idx) => (
                              <span
                                key={idx}
                                className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40 inline-flex items-center space-x-1"
                              >
                                <ShieldCheck className="w-2.5 h-2.5" />
                                <span>Confirmed {anti}-Free</span>
                              </span>
                            ))}
                        </div>
                      )}

                      <div className="pt-2 flex items-center justify-between border-t border-slate-200/60 dark:border-zinc-800/60 mt-0.5">
                        <span className="text-[10px] text-slate-400 dark:text-zinc-500">
                          Check or fix ingredients:
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            triggerHaptic('light');
                            setInspectingRegistryItem(null);
                            onEditMeal?.(meal);
                          }}
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-zinc-800 hover:bg-slate-100 dark:hover:bg-zinc-700 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-slate-200 dark:border-zinc-700 shadow-xs transition-all active:scale-95 cursor-pointer"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          <span>Edit Meal & Fix</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-8 text-center bg-slate-50 dark:bg-zinc-900/40 rounded-2xl border border-dashed border-slate-200 dark:border-zinc-800">
                  <Info className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                  <p className="text-xs font-bold text-slate-800 dark:text-zinc-200">
                    No Direct Meal Logs Found
                  </p>
                </div>
              )}
            </div>

            <div className="p-3.5 sm:p-4 border-t border-slate-100 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/30 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('light');
                  setInspectingRegistryItem(null);
                }}
                className="w-full py-2.5 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-xs transition-all active:scale-95 shadow-xs cursor-pointer"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Doctor Summary Modal */}
      <React.Suspense fallback={null}>
        {isDoctorSummaryOpen && (
          <DoctorSummaryModal
            isOpen={isDoctorSummaryOpen}
            onClose={() => setIsDoctorSummaryOpen(false)}
            meals={meals}
            symptoms={symptoms}
            exercises={exercises}
            activeInsight={insights && insights.length > 0 ? insights[0] : null}
            user={user}
          />
        )}
      </React.Suspense>
    </>
  );

  const renderBalanceToggle = (value: 'day' | 'week', onChange: (v: 'day' | 'week') => void) => (
          <div className="flex items-center bg-slate-100 dark:bg-zinc-800/80 p-0.5 rounded-xl">
            <button
              type="button"
              onClick={() => {
                triggerHaptic('selection');
                onChange('day');
              }}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-black tracking-tight transition-all ${
                value === 'day'
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-2xs'
                  : 'text-slate-400 dark:text-zinc-500 hover:text-slate-700'
              }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                triggerHaptic('selection');
                onChange('week');
              }}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-black tracking-tight transition-all ${
                value === 'week'
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-2xs'
                  : 'text-slate-400 dark:text-zinc-500 hover:text-slate-700'
              }`}
            >
              This Week
            </button>
          </div>
  );

  const renderChartTimeframeToggle = () => (
          <div className="flex items-center bg-slate-100 dark:bg-zinc-800/80 p-0.5 rounded-xl">
            {(['day', '3days', 'week'] as ChartTimeframe[]).map((tf) => (
              <button
                key={tf}
                type="button"
                onClick={() => {
                  triggerHaptic('selection');
                  setSelectedTimeframe(tf);
                  setTimeframeOffset(0);
                }}
                className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-tight transition-all ${
                  selectedTimeframe === tf
                    ? 'bg-white dark:bg-zinc-700 text-slate-900 dark:text-white shadow-2xs'
                    : 'text-slate-400 dark:text-zinc-500'
                }`}
              >
                {tf === 'day' ? '1D' : tf === '3days' ? '3D' : '7D'}
              </button>
            ))}
          </div>
  );

  if (activeAnalyticsSymptomId && !isV2) {
    return (
      <SymptomAnalyticsView
        meals={meals}
        symptoms={symptoms}
        initialSymptomId={activeAnalyticsSymptomId}
        onBack={() => setActiveAnalyticsSymptomId(null)}
        onEditMeal={onEditMeal}
        onEditSymptom={onEditSymptom}
      />
    );
  }

  if (isV2 && activeInsight) {
    const brief = activeInsight.brief;
    const SUB_TITLES: Record<MoreAnalysisPage, string> = {
      report: 'Full report',
      timeline: 'Meal & symptom timeline',
      bowel: 'Bowel movements',
      ingredients: 'All ingredients',
      safe: 'Safe meals',
    };

    const ai = v2View.ai;
    const calculatedText = brief
      ? briefOnlySummary(brief)
      : { headline: 'Not enough data yet.', summary: 'Tap Refresh to analyse your logs.' };
    // Same checks as the stored plan (brief names only, no cut on a rejected lead), applied at read time.
    const plan = brief ? finalPlan(brief, ai) : ai?.plan ?? [];
    const isDay = v2Timeframe === 'day';
    const weekScore = v2Gut
      ? typeof activeInsight.health_score === 'number'
        ? activeInsight.health_score
        : v2Gut.score.score
      : null;

    return (
      <>
        <div
          ref={v2RootRef}
          aria-hidden={coverOpen || undefined}
          className="-mx-4 min-h-full px-4 pt-1 pb-28 bg-slate-100 dark:bg-black select-none"
        >
          <div className="max-w-md mx-auto flex flex-col gap-6 text-left">
            <header className="flex items-center justify-between gap-3 px-1">
              <p className="min-w-0 text-sm text-slate-500 dark:text-zinc-400">{v2Subtitle}</p>
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('light');
                  handleTriggerReanalysis();
                }}
                disabled={isAiRunning || cooldownSeconds > 0}
                aria-label="Refresh insights"
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 disabled:opacity-50 ${PRESS}`}
              >
                <RefreshCw
                  aria-hidden="true"
                  className={`w-3.5 h-3.5 ${isAiRunning ? 'animate-spin motion-reduce:animate-none' : ''}`}
                />
                <span>{cooldownSeconds > 0 ? `${cooldownSeconds}s` : 'Refresh'}</span>
              </button>
            </header>

            <section className="rounded-2xl bg-white dark:bg-zinc-900 p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-950 dark:text-white">Gut score</h2>
                {renderBalanceToggle(v2Timeframe, setV2Timeframe)}
              </div>
              {isDay ? (
                <GutScoreGauge
                  ratio={todayGutBalance.ratio}
                  leftValue={todayGutBalance.calmCount}
                  leftLabel="Calm"
                  leftSub="Check-ins"
                  rightValue={todayGutBalance.flareCount}
                  rightLabel="Flares"
                  rightSub="Discomfort"
                  centerLabel="Comfort Score"
                />
              ) : v2Gut && weekScore !== null ? (
                <GutScoreGauge
                  ratio={weekScore}
                  leftValue={v2Gut.score.stableDays}
                  leftLabel="Stable"
                  leftSub={`${v2Gut.score.loggedDays} Days`}
                  rightValue={v2Gut.score.flareDays}
                  rightLabel="Flare days"
                  rightSub="Recorded"
                  centerLabel="Gut score"
                  reason={v2Gut.reason || undefined}
                />
              ) : (
                <div className="py-10 text-center">
                  <p className="text-base font-semibold text-slate-950 dark:text-white">Not enough data this week</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
                    Log symptoms on at least 3 days to get a gut score.
                  </p>
                </div>
              )}
            </section>

            <InsightSummaryCard
              headline={ai?.headline ?? calculatedText.headline}
              summary={ai?.summary ?? calculatedText.summary}
              calculated={ai == null}
              loading={isAiRunning}
              onRetry={handleTriggerReanalysis}
            />

            <TriggerList components={brief?.componentStats ?? []} ingredients={brief?.ingredientStats ?? []} />
            <PatternList findings={v2View.findings} />
            <WeeklyPlanList plan={plan} />
            <MoreAnalysisList onOpen={openSubPage} />
          </div>
        </div>

        {coverOpen && openPage && (
          <InsightsSubPage
            key={openPage}
            title={SUB_TITLES[openPage]}
            onBack={() => setOpenPage(null)}
          >
            {openPage === 'report' && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic('selection');
                    setIsDoctorSummaryOpen(true);
                  }}
                  className={`flex items-center justify-center gap-2 w-full rounded-2xl bg-white dark:bg-zinc-900 px-4 py-3 text-base font-semibold text-blue-600 dark:text-blue-400 ${PRESS}`}
                >
                  <FileText className="w-4 h-4" aria-hidden="true" />
                  Doctor briefing (PDF)
                </button>
                <div className="rounded-2xl bg-white dark:bg-zinc-900 p-4">
                  {renderClinicalReport(
                    'No written report for this week yet. It is created with each new analysis, and appears here once your logs have been analysed.'
                  )}
                </div>
              </>
            )}
            {openPage === 'timeline' && (
              <>
                {renderTimelineCard()}
                {renderSymptomSquares()}
              </>
            )}
            {openPage === 'bowel' && renderBowelCard()}
            {openPage === 'ingredients' && renderRegistryCard(false)}
            {openPage === 'safe' && renderSafeMealsCard()}
          </InsightsSubPage>
        )}

        {/* Symptom analytics opens over the sub-page, which stays mounted with its scroll intact. */}
        {coverOpen && activeAnalyticsSymptomId && (
          <OverlayLayer className="z-[51]">
            <div className="max-w-md mx-auto px-4">
              <SymptomAnalyticsView
                meals={meals}
                symptoms={symptoms}
                initialSymptomId={activeAnalyticsSymptomId}
                onBack={() => setActiveAnalyticsSymptomId(null)}
                onEditMeal={onEditMeal}
                onEditSymptom={onEditSymptom}
              />
            </div>
          </OverlayLayer>
        )}

        {/* Outside the sub-page so its slide transform never becomes the overlays' containing block. */}
        {renderOverlays()}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-28 pt-1 text-left select-none max-w-md mx-auto">
      {/* Top Clinical Quick Actions */}
      <div className="flex items-center justify-between px-1">
        <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
          Clinical Analytics
        </span>
        <button
          type="button"
          onClick={() => {
            triggerHaptic('selection');
            setIsDoctorSummaryOpen(true);
          }}
          className="flex items-center space-x-1.5 px-3 py-1.5 rounded-2xl bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border border-teal-200/80 dark:border-teal-800/60 text-xs font-black hover:bg-teal-100 dark:hover:bg-teal-900/60 active:scale-95 transition shadow-2xs cursor-pointer"
        >
          <FileText className="w-3.5 h-3.5" />
          <span>Doctor Briefing (PDF)</span>
        </button>
      </div>

      {/* ========================================================================= */}
      {/* CARD 1: GUT BALANCE INDEX (Speedometer Gauge)                             */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center justify-between pb-2.5 mb-2 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Food Log Focus
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Health Balance
              </h2>
            </div>
          </div>

          {renderBalanceToggle(balanceTimeframe, setBalanceTimeframe)}
        </div>

        {/* 3-Column Focus Cluster: Left Metric | Center Gauge | Right Metric */}
        <GutScoreGauge
          ratio={currentGutBalance.ratio}
          leftValue={balanceTimeframe === 'day' ? todayGutBalance.calmCount : weekGutBalance.stableDaysCount}
          leftLabel={balanceTimeframe === 'day' ? 'Calm' : 'Stable'}
          leftSub={balanceTimeframe === 'day' ? 'Check-ins' : `${weekGutBalance.totalDays} Days`}
          rightValue={balanceTimeframe === 'day' ? todayGutBalance.flareCount : weekGutBalance.flareDaysCount}
          rightLabel={balanceTimeframe === 'day' ? 'Flares' : 'Sensitive'}
          rightSub={balanceTimeframe === 'day' ? 'Discomfort' : 'Recorded'}
          centerLabel={balanceTimeframe === 'day' ? 'Comfort Score' : 'Health Balance'}
        />

        {/* 3 Metric Breakdown Bars */}
        <div className="w-full grid grid-cols-3 gap-2 pt-2.5 border-t border-slate-100 dark:border-zinc-800/80">
          <div className="p-2 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/60 dark:border-zinc-800/80 text-left">
            <div className="flex items-center justify-between text-[10px] text-slate-400 dark:text-zinc-500 font-bold mb-1">
              <span>Bloat</span>
              <span className="text-slate-900 dark:text-white font-black">{currentGutBalance.avgBloat}/10</span>
            </div>
            <div className="w-full bg-slate-200/70 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  currentGutBalance.avgBloat <= 3
                    ? 'bg-blue-500'
                    : currentGutBalance.avgBloat <= 6
                    ? 'bg-amber-500'
                    : 'bg-rose-500'
                }`}
                style={{ width: `${Math.min(100, Math.max(10, (currentGutBalance.avgBloat / 10) * 100))}%` }}
              />
            </div>
          </div>

          <div className="p-2 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/60 dark:border-zinc-800/80 text-left">
            <div className="flex items-center justify-between text-[10px] text-slate-400 dark:text-zinc-500 font-bold mb-1">
              <span>Soreness</span>
              <span className="text-slate-900 dark:text-white font-black">{currentGutBalance.avgSoreness}/10</span>
            </div>
            <div className="w-full bg-slate-200/70 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  currentGutBalance.avgSoreness <= 3
                    ? 'bg-blue-500'
                    : currentGutBalance.avgSoreness <= 6
                    ? 'bg-amber-500'
                    : 'bg-rose-500'
                }`}
                style={{ width: `${Math.min(100, Math.max(10, (currentGutBalance.avgSoreness / 10) * 100))}%` }}
              />
            </div>
          </div>

          <div className="p-2 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-200/60 dark:border-zinc-800/80 text-left">
            <div className="flex items-center justify-between text-[10px] text-slate-400 dark:text-zinc-500 font-bold mb-1">
              <span className="whitespace-nowrap">Stool Form</span>
              <span className="text-slate-900 dark:text-white font-black">{currentGutBalance.stoolOptimalPercent}%</span>
            </div>
            <div className="w-full bg-slate-200/70 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${Math.min(100, Math.max(0, currentGutBalance.stoolOptimalPercent))}%` }}
              />
            </div>
          </div>
        </div>

        <p className="w-full text-xs text-slate-500 dark:text-zinc-400 mt-2.5 font-medium leading-relaxed text-left">
          {currentGutBalance.description}
        </p>
      </div>

      {/* ========================================================================= */}
      {/* CARD 2: INTERACTIVE MEAL-TO-SYMPTOM TIMELINE VISUALIZER                   */}
      {/* ========================================================================= */}
      {renderTimelineCard()}

      {/* ========================================================================= */}
      {/* CARD 3: SYMPTOM ANALYTICS SQUARE WIDGETS                                  */}
      {/* ========================================================================= */}
      {renderSymptomSquares()}

      {/* ========================================================================= */}
      {/* CARD 3.5: BOWEL MOVEMENT & MOTILITY KINETICS (Constipation & Bloat)      */}
      {/* ========================================================================= */}
      {renderBowelCard()}

      {/* ========================================================================= */}
      {/* CARD 4: CLINICAL 4–8 WEEK INTOLERANCE REGISTRY (With Cross-Referencing)   */}
      {/* ========================================================================= */}
      {renderRegistryCard(true)}

      {/* ========================================================================= */}
      {/* CARD 5: PROACTIVE GUT RECOVERY & SAFE FOUNDATION MEALS                    */}
      {/* ========================================================================= */}
      {renderSafeMealsCard()}

      {/* ========================================================================= */}
      {/* CARD 6: DIGESTIVE INSIGHT (Clinical AI Narrative)                         */}
      {/* ========================================================================= */}
      <div className="p-5 rounded-3xl bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 shadow-xs">
        <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 rounded-xl bg-slate-100 dark:bg-zinc-800 text-slate-900 dark:text-white">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                Clinical Synthesis
              </span>
              <h2 className="text-sm font-black text-slate-950 dark:text-white tracking-tight">
                Digestive Insight
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={handleTriggerReanalysis}
            disabled={isAiRunning || cooldownSeconds > 0}
            className="flex items-center space-x-1 px-2.5 py-1 rounded-xl bg-slate-100 dark:bg-zinc-800 text-[11px] font-bold text-slate-700 dark:text-zinc-300 hover:text-slate-950 dark:hover:text-white active:scale-95 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${isAiRunning ? 'animate-spin' : ''}`} />
            <span>{cooldownSeconds > 0 ? `${cooldownSeconds}s` : 'Refresh'}</span>
          </button>
        </div>

        {renderClinicalReport()}
      </div>

      {renderOverlays()}
    </div>
  );
});
